import { XMarkIcon } from '@heroicons/react/24/solid';
import React from 'react';
import { StartMonitoringForm } from '../patient/actions/StartMonitoringForm';
import { StartMonitoringPatient } from '../../services/patient';
import { StartMonitoringParams } from '../../services/patient/delivery';

interface StartMonitoringModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (params: StartMonitoringParams) => void;
    patient: StartMonitoringPatient | null;
    isSubmitting?: boolean;
    showAlignerNumbers?: boolean;
}

/**
 * Modal chrome (overlay, title, close button) around `StartMonitoringForm`.
 * Used by `PatientActionsContext`'s "Start monitoring" workflow entry point
 * (full flow: phase selection + aligner numbers, paused patients resume with
 * their existing quickstart). The doctor-overview "start" flow lives inside
 * `ManageMonitoringModal` instead — see `StartMonitoringForm` for the shared
 * body.
 */
const StartMonitoringModal: React.FC<StartMonitoringModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    patient,
    isSubmitting = false,
    showAlignerNumbers = true
}) => {
    if (!isOpen) return null;

    // Mirrors StartMonitoringForm's own paused check — only used here to pick
    // the header title ("Start" vs "Resume").
    const isPaused = (patient?.monitoring?.monitoringStatus ?? '').toLowerCase() === 'paused';

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={isSubmitting ? undefined : onClose}
        >
            <div
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800">
                        {isPaused ? 'Resume Monitoring' : 'Start Monitoring'}
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={isSubmitting}
                        className={`${isSubmitting ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-800'}`}
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <StartMonitoringForm
                    patient={patient}
                    onConfirm={onConfirm}
                    onCancel={onClose}
                    isSubmitting={isSubmitting}
                    showAlignerNumbers={showAlignerNumbers}
                />
            </div>
        </div>
    );
};

export default StartMonitoringModal;
